import React from 'react';
import { Button } from './ui/Button';
import { ArrowRight, CheckCircle2, Star, Shield } from 'lucide-react';
interface HeroSectionProps {
  title: string;
  subtitle: string;
  primaryCtaText: string;
  secondaryCtaText: string;
  onPrimaryClick: () => void;
  onSecondaryClick: () => void;
  benefits?: string[];
}
export function HeroSection({
  title,
  subtitle,
  primaryCtaText,
  secondaryCtaText,
  onPrimaryClick,
  onSecondaryClick,
  benefits = []
}: HeroSectionProps) {
  return <section className="relative overflow-hidden bg-white pt-20 pb-24 lg:pt-32 lg:pb-40" data-id="element-171">
      {/* Background Gradients */}
      <div className="absolute top-0 right-0 -z-10 h-[600px] w-[600px] rounded-full bg-blue-50 blur-3xl opacity-60 translate-x-1/3 -translate-y-1/4" data-id="element-172"></div>
      <div className="absolute bottom-0 left-0 -z-10 h-[600px] w-[600px] rounded-full bg-indigo-50 blur-3xl opacity-60 -translate-x-1/3 translate-y-1/4" data-id="element-173"></div>

      <div className="container mx-auto px-4 md:px-6" data-id="element-174">
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-12 items-center" data-id="element-175">
          <div className="flex flex-col justify-center space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700" data-id="element-176">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-1.5 text-sm font-medium text-blue-700 w-fit border border-blue-100" data-id="element-177">
              <Shield className="h-4 w-4 fill-blue-600 text-blue-600" data-id="element-178" />
              <span data-id="element-179">Deutschlands führende Versicherungs-App</span>
            </div>

            <div className="space-y-6" data-id="element-180">
              <h1 className="text-5xl font-bold tracking-tight text-slate-900 sm:text-6xl xl:text-7xl/none leading-tight" data-id="element-181">
                {title.split('. ')[0]}. <br data-id="element-182" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600" data-id="element-183">
                  {title.split('. ')[1]}
                </span>
              </h1>
              <p className="max-w-[600px] text-xl text-slate-600 md:text-2xl leading-relaxed" data-id="element-184">
                {subtitle}
              </p>
            </div>

            {benefits.length > 0 && <ul className="space-y-3" data-id="element-185">
                {benefits.map((benefit, index) => <li key={index} className="flex items-center gap-3 text-slate-700 font-medium" data-id="element-186">
                    <div className="rounded-full bg-green-100 p-1" data-id="element-187">
                      <CheckCircle2 className="h-4 w-4 text-green-600" data-id="element-188" />
                    </div>
                    <span data-id="element-189">{benefit}</span>
                  </li>)}
              </ul>}

            <div className="flex flex-col gap-4 sm:flex-row pt-4" data-id="element-190">
              <Button size="lg" variant="premium" onClick={onPrimaryClick} className="gap-2 text-lg px-8 h-14 shadow-xl shadow-blue-500/20" data-id="element-191">
                {primaryCtaText} <ArrowRight className="h-5 w-5" data-id="element-192" />
              </Button>
              <Button size="lg" variant="outline" onClick={onSecondaryClick} className="text-lg px-8 h-14 border-2" data-id="element-193">
                {secondaryCtaText}
              </Button>
            </div>

            <div className="flex items-center gap-6 text-sm text-slate-500 pt-4 border-t border-slate-100 w-fit" data-id="element-194">
              <div className="flex -space-x-3" data-id="element-195">
                {[1, 2, 3, 4].map(i => <div key={i} className="h-10 w-10 rounded-full border-2 border-white bg-slate-200 shadow-sm" data-id="element-196" />)}
              </div>
              <div className="flex flex-col" data-id="element-197">
                <div className="flex items-center gap-1" data-id="element-198">
                  <div className="flex text-amber-400" data-id="element-199">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} className="h-4 w-4 fill-current" data-id="element-200" />)}
                  </div>
                  <span className="font-bold text-slate-900" data-id="element-201">4.9/5</span>
                </div>
                <p data-id="element-202">Über 50.000 zufriedene Kunden</p>
              </div>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-[600px] lg:max-w-none animate-in fade-in slide-in-from-right-8 duration-1000 delay-200" data-id="element-203">
            <div className="relative aspect-[4/3] overflow-hidden rounded-3xl bg-white shadow-2xl border border-slate-100" data-id="element-204">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-slate-50" data-id="element-205"></div>
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-206">
                {/* Abstract UI Representation */}
                <div className="w-3/4 h-3/4 bg-white rounded-xl shadow-lg border border-slate-100 p-6 flex flex-col gap-4 relative z-10" data-id="element-207">
                  <div className="h-8 w-1/3 bg-slate-100 rounded-lg animate-pulse" data-id="element-208"></div>
                  <div className="flex gap-4" data-id="element-209">
                    <div className="h-32 w-1/2 bg-blue-50 rounded-lg border border-blue-100" data-id="element-210"></div>
                    <div className="h-32 w-1/2 bg-indigo-50 rounded-lg border border-indigo-100" data-id="element-211"></div>
                  </div>
                  <div className="h-4 w-full bg-slate-50 rounded animate-pulse" data-id="element-212"></div>
                  <div className="h-4 w-2/3 bg-slate-50 rounded animate-pulse" data-id="element-213"></div>

                  {/* Floating Badge */}
                  <div className="absolute -right-6 -top-6 bg-white p-4 rounded-xl shadow-xl border border-slate-100 flex items-center gap-3 animate-bounce duration-[3000ms]" data-id="element-214">
                    <div className="bg-green-100 p-2 rounded-full" data-id="element-215">
                      <Shield className="h-6 w-6 text-green-600" data-id="element-216" />
                    </div>
                    <div data-id="element-217">
                      <p className="text-xs text-slate-500" data-id="element-218">Ersparnis</p>
                      <p className="font-bold text-slate-900" data-id="element-219">450 € / Jahr</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -bottom-12 -left-12 -z-10 h-72 w-72 rounded-full bg-blue-600/10 blur-3xl" data-id="element-220"></div>
            <div className="absolute -top-12 -right-12 -z-10 h-72 w-72 rounded-full bg-amber-400/10 blur-3xl" data-id="element-221"></div>
          </div>
        </div>
      </div>
    </section>;
}